import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, PageEvent } from '@angular/material';
import { FcPaginatorComponent } from '@core/components/fc-paginator/fc-paginator.component';
import { Group, Location, PagedResult } from '@core/interfaces';
import { Subject } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GroupService } from '../../../services/groups-api.service';
import { LinkLocationsComponent } from './link-locations/link-locations.component';

@Component({
  selector: 'tab-locations',
  templateUrl: './tab-locations.component.html',
  styleUrls: ['./tab-locations.component.scss']
})
export class TabLocationsComponent implements OnChanges, OnDestroy, OnInit {

  @Input()
  public isViewMode: boolean;
  @Input()
  public group: Group;

  public searchInput = new FormControl('');
  public page: PagedResult<Location> = null;
  public isSearching = false;
  public pageSize = 5;
  public currentPage = 0;
  public isLoading = true;
  public isPageChanging = false;

  @ViewChild('paginator', { static: false })
  public paginator: FcPaginatorComponent;

  private readonly removeSubscriptions = new Subject<void>();
  private readonly reload = new Subject<void>();

  constructor(
    private readonly matDialog: MatDialog,
    private readonly groupService: GroupService
  ) {}

  public ngOnInit(): void {
    this.searchInput.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.isLoading = true;
      if (this.paginator) {
        this.paginator.firstPage();
      } else {
        this.reload.next();
      }
    });

    this.reload.pipe(
      takeUntil(this.removeSubscriptions),
      tap(() => this.isLoading = true),
      switchMap(() => this.searchLocations()),
      tap(() => {
        this.isLoading = false;
      })
    ).subscribe((locationPage) => {
      this.page = locationPage;
    });

    this.reload.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.group) {
      this.reload.next();
    }
  }

  public ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  public openLinkLocationsDialog(): void {
    this.matDialog
      .open(LinkLocationsComponent, {
        panelClass: ['dialog-groups'],
        data: {
          group: this.group,
        },
      })
      .beforeClosed()
      .pipe(filter((result) => result && result.reload))
      .subscribe(() => {
        this.reload.next();
      });
  }

  private async searchLocations(): Promise<PagedResult<Location>> {
    if (!this.group.id) {
      return null;
    }

    return this.groupService.listLocations({
      groupId: this.group.id,
      limit: this.pageSize,
      offset: this.currentPage * this.pageSize,
      where: {
        formattedAddressOrDocumentNumber_contains: this.searchInput.value || '',
        groupId_in: [this.group.id]
      }
    });
  }

  public async removeLocationFroGroup(location: Location): Promise<void> {
    location['isSaving'] = true;
    await this.groupService.removeLocationFromGroup([
      {
        locationId: location.id,
        groupId: this.group.id
      }
    ]);
    this.page = await this.searchLocations();
    location['isSaving'] = false;
  }

  async onPageChange(event: PageEvent): Promise<void> {
    this.currentPage = event.pageIndex;
    this.isPageChanging = true;
    this.page = await this.searchLocations();
    this.isPageChanging = false;
    this.isLoading = false;
  }
}
